import lodashMerge from 'lodash/merge';
import queryString from 'query-string';

import {assertDependencies} from '../lib/util';
import {EMIT as SCENE_OBSERVER_EMIT} from '../scene_observer/scene_observer';

const MANIFEST = require('./manifest');

const DEFAULT_OPTIONS = {
	enabled: false,
	shortcut_key: 83, // s
	journey_spot_size: 100,
};

const faces = [
	['front',     0,   0],
	['right',    90,   0],
	['back',    180,   0],
	['left',    -90,   0],
	['up',        0, -90],
	['down',      0,  90],
];

// TODO: this is getting ridiculous
// TODO: just define the allowed keys and keep in sync with api
const SKIP_QUERY_KEYS = [
	/^s$/,
	'html5',
	'insights',
	'consolelog',
	'display.',
	'skipintro',
];

function KrPanoInsights(options = DEFAULT_OPTIONS, deps = MANIFEST.dependencies) {
	const thisKrPanoInsights = this;

	options = lodashMerge({}, DEFAULT_OPTIONS, options);
	assertDependencies(MANIFEST.name, MANIFEST.dependencies, deps);

	const log = deps.logger.child(MANIFEST.name);

	const apiBaseUrl = options.base_url || deps.SPINVIEW_OPTIONS.AudienceTrackerApi.base_url;
	let baseUrl = apiBaseUrl[apiBaseUrl.length - 1] === '/'
		? apiBaseUrl.slice(0, -1)
		: apiBaseUrl;
	let gazeBaseUrl = baseUrl + '/api/v1/reporting/audience/gaze.png';
	let journeyBaseUrl = baseUrl + '/api/v1/reporting/audience/journey/exits';

	let _enabled = options.enabled;
	let _cube = [];
	let _journeySpots = [];

	Object.assign(thisKrPanoInsights, /** @lends KrPanoInsights.prototype */ {
		_cube,
		_journeySpots,
		init,
		isEnabled: () => _enabled,
		enable: (feature) => setEnabled(true, feature),
		disable: (feature) => setEnabled(false, feature),
		makeHeatmapImageUrl,
		showHeatmap,
		hideHeatmap,
		showJourneySpots,
		hideJourneySpots,
	});

	function init() {
		if (options.shortcut_key) {
			$('body').on('keydown', function(e) {
				if (e.ctrlKey && e.altKey && e.keyCode === options.shortcut_key) {
					setEnabled(!_enabled);
				}
			});
		}

		deps.sceneObserver.on(SCENE_OBSERVER_EMIT.entered_scene, handleSceneChange);

		log.verbose('Initialized');
		setEnabled(_enabled);
	}

	/**
	 *
	 * @param {boolean} val
	 * @param {"heatmap"|"route"} [feature]
	 */
	function setEnabled(val, feature) {
		_enabled = val;
		log.info('Insights: heatmaps have been ' + (val ? 'ENABLED' : 'DISABLED'));
		if (_enabled) {
			if (feature === "heatmap" || !feature) showHeatmap();
			if (feature === "route" || !feature) showJourneySpots();
		} else {
			if (feature === "heatmap" || !feature) hideHeatmap();
			if (feature === "route" || !feature) hideJourneySpots();
		}
	}

	/**
	 * @param {SceneInfo} current
	 * @param {SceneInfo} prev
	 */
	function handleSceneChange(current, prev) {
		hideHeatmap();
		hideJourneySpots(prev.name);

		showHeatmap();
		setTimeout(() => showJourneySpots(current.name), 300);
	}

	function showHeatmap(sceneName) {
		if (!_enabled) {
			return;
		}
		const krpano = deps.krPanoClient.getKRPano();
		if (!krpano) {
			return setTimeout(showHeatmap, 500, ...arguments);
		}

		sceneName = sceneName || krpano.get('xml.scene');
		if (_cube.length) {
			removeCube(krpano, _cube);
		}
		const cube = addCube(krpano, sceneName);
		cube.forEach(hotspot => _cube.push(hotspot));
	}

	function hideHeatmap() {
		const krpano = deps.krPanoClient.getKRPano();
		if (!krpano) {
			return setTimeout(hideHeatmap, 500, ...arguments);
		}
		if (_cube.length) {
			removeCube(krpano, _cube);
		}
	}

	function showJourneySpots(sceneName) {
		if (!_enabled) {
			return;
		}
		const krpano = deps.krPanoClient.getKRPano();
		if (!krpano) {
			return setTimeout(showJourneySpots, 500, ...arguments);
		}

		sceneName = sceneName || krpano.get('xml.scene');
		if (_journeySpots.length) {
			removeJourneySpots(krpano, _journeySpots);
		}
		const journeySpots = addJourneySpots(krpano, sceneName);
		journeySpots.forEach(hotspot => _journeySpots.push(hotspot));
	}

	function hideJourneySpots() {
		const krpano = deps.krPanoClient.getKRPano();
		if (!krpano) {
			return setTimeout(hideJourneySpots, 500, ...arguments);
		}
		if (_journeySpots.length) {
			removeJourneySpots(krpano, _journeySpots);
		}
	}

	function removeCube(krpano, cube) {
		let hotspot;
		while ((hotspot = cube.pop())) {
			const name = hotspot.name;
			krpano.removehotspot(name);
			krpano.removehotspot(name + '_spinner');
		}
	}

	function removeJourneySpots(krpano, journeySpots) {
		let hotspot;
		let sceneName = krpano.get('xml.scene');
		while ((hotspot = journeySpots.pop())) {
			const name = hotspot.name;
			const originalSceneName = hotspot.original_scene_name;
			if (originalSceneName === sceneName) {
				krpano.set('hotspot[' + hotspot.original_spot_name + '].visible', true);
			}
			krpano.removehotspot(name);
			krpano.removehotspot(name + '_spinner');
		}
	}

	function addCube(krpano, sceneName) {
		const siteId = deps.SPINVIEW_OPTIONS.AudienceTracker.site_id;

		sceneName = sceneName || krpano.get('xml.scene');
		const imageSize = 1000;
		const cube = [];
		for (let i = 0; i < faces.length; i++) {
			const face = faces[i][0];
			const ath = faces[i][1];
			const atv = faces[i][2];
			const spotName = sceneName + '_' + face;
			const url = makeHeatmapImageUrl(gazeBaseUrl, siteId, sceneName, imageSize, face);

			const spinner = deps.krPanoClient.addSpinner(spotName + '_spinner', {ath, atv, tooltip: 'Loading heatmap'});
			const hotspot = addHotspot(krpano, spotName, url, 1000, ath, atv);
			hotspot.onloaded = hotspot.onloaderror = () => {
				spinner.removeSpinner();
				hotspot.onloaded = hotspot.onloaderror = undefined;
			};
			cube.push(hotspot);
		}
		return cube;
	}

	function addJourneySpots(krpano, sceneName, spotSize) {
		const siteId = deps.SPINVIEW_OPTIONS.AudienceTracker.site_id;

		let hotspots = deps.krPanoClient.getHotspots().filter(h => h._raw.linktarget);

		sceneName = sceneName || krpano.get('xml.scene');

		const journeySpots = [];

		hotspots.forEach(navSpot => {
			const rawSpot = navSpot._raw;
			const targetScene = rawSpot.linktarget;
			const ath = rawSpot.ath;
			const atv = rawSpot.atv;

			const spotName = sceneName + '_' + targetScene + '_exits';
			const size = spotSize || rawSpot.imagewidth || options.journey_spot_size;
			const url = makeJourneyImageUrl(journeyBaseUrl, siteId, sceneName, targetScene, spotSize);

			const spinner = deps.krPanoClient.addSpinner(spotName + '_spinner', {ath, atv, tooltip: 'Loading journey data'});
			const hotspot = addHotspot(krpano, spotName, url, size, ath, atv, rawSpot.style);

			hotspot.visible = true;
			hotspot.enabled = true;
			hotspot.distorted = rawSpot.distorted;
			spinner.ath = hotspot.ath;
			spinner.atv = hotspot.atv;
			hotspot.onclick = rawSpot.onclick;
			hotspot.width = rawSpot.width;
			hotspot.height = rawSpot.height;
			hotspot.crop = rawSpot.crop;
			hotspot.zoom = rawSpot.zoom;
			hotspot.original_spot_name = rawSpot.name;
			hotspot.original_scene_name = sceneName;

			hotspot.onloaded = hotspot.onloaderror = () => {
				spinner.removeSpinner();
				hotspot.onloaded = hotspot.onloaderror = undefined;
				rawSpot.visible = false;
				hotspot.width = spotSize || options.journey_spot_size;
				hotspot.height = spotSize || options.journey_spot_size;

				hotspot.crop = null;
			};

			journeySpots.push(hotspot);
		});

		return journeySpots;
	}

	function addHotspot(krpano, name, url, size, ath, atv, style = null) {
		// desktop html5 browsers need overlapping cube faces
		// if (krpano.get('device.html5') && krpano.get('device.desktop')) {
		// 	size = size + 2;
		// 	size = size + 2;
		// }
		const hotspot = krpano.addhotspot(name);
		if (style) {
			hotspot.loadstyle(style);
		}

		hotspot.url = url;
		hotspot.ath = ath;
		hotspot.atv = atv;
		hotspot.width = size;
		hotspot.height = size;

		hotspot.distorted = true;
		hotspot.enabled = false;

		if (krpano.get('isFlash')) {
			hotspot.visible = false;
		}

		// hotspot.alpha = 0.0;
		// hotspot.details = 16;
		return hotspot;
	}

	function makeHeatmapImageUrl(apiBaseUrl, siteId, sceneName, imageSize, face, criteria) {
		const url = apiBaseUrl;
		const query = criteriaToQuery(imageSize, criteria);

		query.site = siteId;
		query.location = sceneName;
		query.bare = true;
		if (face) {
			query.face = face;
		}

		return url + '?' + queryString.stringify(query);
	}


	function makeJourneyImageUrl(apiBaseUrl, siteId, origin, location, imageSize, criteria) {
		const url = apiBaseUrl;
		const query = criteriaToQuery(imageSize, criteria);

		query.site = siteId;
		query.origin = origin;
		query.location = location;

		return url + '?' + queryString.stringify(query);
	}

	function criteriaToQuery(imageSize, criteria) {
		if (!criteria) {
			criteria = {};
		}

		const qs = queryString.parse(deps.location.search);
		const query = {};
		Object.keys(qs).forEach(key => {
			const keyIgnored = SKIP_QUERY_KEYS.find(reg => {
				return reg instanceof RegExp ? key.match(reg) : key.includes(reg);
			});
			if (keyIgnored) {
				return false;
			}
			query[key] = qs[key];
		});

		if (typeof criteria.toQuery === 'function') {
			criteria = criteria.toQuery();
		}
		const from = criteria.from;
		const to = criteria.to;
		if (from instanceof Date) {
			query.from = from.toISOString();
		}
		if (to instanceof Date) {
			query.to = to.toISOString();
		}
		if (imageSize) {
			query.size = imageSize + 'x' + imageSize;
		}

		return query;
	}
}

// *********************************************************************************************************************

export default KrPanoInsights;
